import { useFormContext } from "react-hook-form";
import { FC, useContext, useEffect } from "react";
import { QuestionTypeSelect } from "./QuestionTypeSelect";
import { QuestionDescription } from "./QuestionDescription";
import { AnswersField } from "./AnswersField";
import { IAnswer, QUESTION_TYPES } from "src/pages/DashboardPage/acadia/components/Tests/types";
import AddAnswerButton from "./BtnAddAnswer";
import { TestRegistryContext } from "../../store/testRegistryContext";
import { FormFooter } from "./FormFooter";
import { answersRegistry, NotificationsListFieldsRecordType, questionsRegistry, testRegistry } from "src/api/config/notifications.config";
import { addItemRequest, deleteItemRequest, updateItemRequest } from "src/modules/spItems/api";
import { setAddQuestion, setOpenForm, setTest } from "../../store/testRegistryActions";

interface IProps {
  answersQuestion?: IAnswer[];
  question?: NotificationsListFieldsRecordType["QuestionsRegistry"];
}

export const FormQuestion: FC<IProps> = ({ answersQuestion, question }) => {
  const { watch, setValue, handleSubmit } = useFormContext();
  const { state, dispatchTestRegistry } = useContext(TestRegistryContext);
  const answers: IAnswer[] = watch("AnswersTest");

  useEffect(() => {
    if (!state.addQuestion) {
      if (answersQuestion && question) {
        setValue("AnswersTest", answersQuestion);
        setValue("DescriptionQuestion", question.Description);
        setValue("TypeQuestion", question.TypeQuestion);
      }
    } else {
      setValue("AnswersTest", [
        {
          ID: new Date().getTime().toString(),
          Description: "",
          IsCorrect: false
        },
        {
          ID: new Date().getTime().toString() + 1,
          Description: "",
          IsCorrect: false
        }
      ]);
      setValue("TypeQuestion", QUESTION_TYPES.MULTIPLE);
      setValue("DescriptionQuestion", "");
    }
  }, [answersQuestion, question, setValue]);

  const updateQuestions = (questions: any[]) => {
    dispatchTestRegistry(
      setTest({
        ...state.test,
        QuestionsID: questions
      })
    );
  };

  const updateTest = async (data: any) => {
    let newAnswersID: Array<string | number> = [];
    const promises: Promise<any>[] = [];

    const newAnswers: { Title: string; Description: string; ID: number | string; IsCorrect: boolean }[] = data.AnswersTest.map((answer: any) => {
      return {
        Title: answer.Description,
        Description: answer.Description,
        ID: answer.ID,
        IsCorrect: answer.IsCorrect
      };
    });

    const oldAnswers = question?.Answers;
    if (!oldAnswers) return;
    try {
      newAnswers.forEach(data => {
        const foundAnswer = oldAnswers?.find(({ ID }: any) => ID === data.ID);
        const body = {
          Title: data.Description,
          Description: data.Description,
          ID: data.ID,
          IsCorrect: data.IsCorrect
        };
        if (foundAnswer) {
          updateItemRequest({
            listName: answersRegistry,
            body: body,
            update: false
          });
          newAnswersID.push(data.ID);
          return;
        }
        const answerID = addItemRequest({
          listName: answersRegistry,
          body: body,
          updateReducer: false
        });
        promises.push(answerID);
      });

      oldAnswers?.forEach(({ ID }: any) => {
        const foundAnswer = newAnswers.find(({ ID: newID }) => ID === newID);
        if (!foundAnswer) {
          deleteItemRequest({ listName: answersRegistry, ID: ID });
        }
      });

      const resultAnswers = await Promise.all(promises);
      newAnswersID = [...newAnswersID, ...resultAnswers];

      const questionCorrect = {
        TypeQuestion: data.TypeQuestion,
        Description: data.DescriptionQuestion,
        Title: data.DescriptionQuestion,
        Answers: newAnswersID,
        ID: question?.ID
      };

      updateItemRequest({
        listName: questionsRegistry,
        body: questionCorrect,
        update: false
      }).then(() => {
        dispatchTestRegistry(setOpenForm(false));
        dispatchTestRegistry(setAddQuestion(false));
        dispatchTestRegistry(
          setTest({
            ...state.test,
            QuestionsID: state.test.QuestionsID
          })
        );
      });
    } catch (e) {}
  };

  const onSubmit = async (data: any) => {
    if (!state.addQuestion) {
      updateTest(data);
    }

    if (state.addQuestion) {
      let answersID = [];
      let questionsID = [];
      try {
        for (const answer of data.AnswersTest) {
          const registryAnswersID = await addItemRequest({
            listName: answersRegistry,
            body: { ...answer, Title: answer.Description },
            updateReducer: false
          });
          answersID.push(registryAnswersID);
        }
        const registryQuestionID = await addItemRequest({
          listName: questionsRegistry,
          body: {
            ID: new Date().getTime().toString(),
            Description: data.DescriptionQuestion,
            Answers: answersID,
            Title: data.DescriptionQuestion,
            TypeQuestion: data.TypeQuestion
          },
          updateReducer: false
        });
        questionsID.push(registryQuestionID);
        answersID.length = 0;
        updateItemRequest({
          listName: testRegistry,
          body: { ...state.test, Collections: state.test.Collections.ID, QuestionsID: [...state.test.QuestionsID, ...questionsID] },
          update: false
        });

        updateQuestions([...state.test.QuestionsID, ...questionsID]);
        questionsID.length = 0;
        dispatchTestRegistry(setOpenForm(false));
        dispatchTestRegistry(setAddQuestion(false));
      } catch (e) {
        console.log(e);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <QuestionTypeSelect />
      <QuestionDescription />
      {answers?.map((answer, index) => (
        <AnswersField key={answer.ID} id={answer.ID} answer={answer} index={index} />
      ))}
      <AddAnswerButton />
      <FormFooter question={question} />
    </form>
  );
};
